/**
 * Copy actions for the table tile (S4) — cell/row/table, reusing the SAME
 * clipboard utils as the Query Editor/Table Explorer grids (G12: don't
 * reinvent). Fires `feature:dashboard_tile_copy { scope }` on success, per
 * repo analytics rule (fire after the side-effect, not on click).
 */
import { useCallback } from 'react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import {
  resultSetToCsv,
  resultSetToHtml,
  writeRichToClipboard,
} from '../../../common/utils/clipboardFormatters';
import type { QueryResultSet } from '../../../../../types/queryExecution';

function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

export function useTileTableCopy() {
  const { track } = useAnalytics();

  const copyTable = useCallback(
    async (resultSet: QueryResultSet) => {
      await writeRichToClipboard(resultSetToCsv(resultSet), resultSetToHtml(resultSet));
      track('feature:dashboard_tile_copy', { scope: 'table' });
    },
    [track],
  );

  const copyRow = useCallback(
    async (columns: QueryResultSet['columns'], row: Record<string, unknown>) => {
      const rowResultSet: QueryResultSet = { columns, rows: [row] };
      await writeRichToClipboard(resultSetToCsv(rowResultSet), resultSetToHtml(rowResultSet));
      track('feature:dashboard_tile_copy', { scope: 'row' });
    },
    [track],
  );

  const copyCell = useCallback(
    async (value: unknown) => {
      await writeRichToClipboard(cellText(value), '');
      track('feature:dashboard_tile_copy', { scope: 'cell' });
    },
    [track],
  );

  return { copyTable, copyRow, copyCell };
}
